/** @format */

import { Injectable, inject } from '@angular/core';

import { CookieService } from './cookie.service';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ScorecardPlusConfigService } from '@scorecard/config';

interface DecodedCartItem {
  sku: string;
}

interface DecodedCart {
  contents: DecodedCartItem[];
}

@Injectable({
  providedIn: 'root',
})
export class CartDecoderService {
  private readonly cookieService = inject(CookieService);
  private readonly scorecardPlusConfigService = inject(ScorecardPlusConfigService);

  readonly isCartDataStorageEnabled = false;

  getDecodedCartCookie(): DecodedCartItem[] | null {
    const cartCookie = this.getCartCookieSource();

    if (!cartCookie) {
      return null;
    }

    return this.parseCartCookie(cartCookie);
  }

  isPaidTierInCart(): boolean {
    const items = this.getDecodedCartCookie() || [];
    return items.some((item) => this.scorecardPlusConfigService.isScoreCardPlusSku(item.sku)); // Defaults to environment variable SKU
  }

  private getCartCookieSource(): string | null {
    return this.isCartDataStorageEnabled ? this.getFromLocalStorage() : this.getFromCookie();
  }

  private getFromCookie(): string | null {
    return this.cookieService.get(DCSG_CONSTANTS.cookies.cart.contents.cookieName);
  }

  private getFromLocalStorage(): string | null {
    return null;
  }

  private parseCartCookie(cartCookie: string): DecodedCartItem[] | null {
    try {
      const decodedCart = atob(cartCookie);
      const cartObject: unknown = JSON.parse(decodedCart);

      if (this.isValidCartObject(cartObject)) {
        return cartObject.contents;
      }

      return null;
    } catch {
      return null;
    }
  }

  private isValidCartObject(obj: unknown): obj is DecodedCart {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'contents' in obj &&
      Array.isArray(obj.contents) &&
      obj.contents.every(
        (item: unknown) =>
          typeof item === 'object' &&
          item !== null &&
          'sku' in item &&
          typeof item.sku === 'string',
      )
    );
  }
}
